import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Plus, Calendar, Package, FileText, FolderOpen, Edit, MapPin, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import DocumentUploadModal from '../components/DocumentUploadModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HSE_CATEGORIES = {
  'idoneita_sanitarie': '1. Idoneità Sanitarie',
  'corso_formazione_generale': '2. Corso formazione generale e specifica',
  'lavori_quota_dpi': '3. Lavori in quota e DPI di III Cat',
  'moduli_consegna_dpi': '4. Moduli consegna DPI',
  'primo_soccorso_antincendio': '5. Primo Soccorso e Antincendio',
  'preposto': '6. Preposto',
  'ple_sollevatori_gru': '7. PLE-Sollevatori-Gru',
  'pes_pav_pei': '8. PES-PAV-PEI',
  'qualifica_fer': '9. Qualifica FER',
  'fgas': '10. FGas',
  'corsi_main_solution': '11. Corsi Main Solution',
  'rls': '12. RLS',
  'rlsa': '13. RLSA',
  'rspp': '14. RSPP',
  'medico_competente': '15. Medico Competente',
  'tesserini_cantiere': '16. Tesserini cantiere',
  'fatture_dpi': '17. Fatture DPI',
  'unilav': '18. Unilav',
  'carte_identita': '19. Carte Identità',
  'lavoratori_autonomi': '20. Lavoratori autonomi',
  'altro': 'Altro'
};

function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dati');
  const [appointments, setAppointments] = useState([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [compliance, setCompliance] = useState([]);
  const [assets, setAssets] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingCompliance, setEditingCompliance] = useState(null);
  const [editComplianceForm, setEditComplianceForm] = useState({});
  
  useEffect(() => {
    // Get current user
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
    fetchEmployeeData();
  }, [id]);
  
  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      
      const empRes = await fetch(`${API_URL}/api/employees/${id}`);
      if (!empRes.ok) {
        navigate('/employees');
        return;
      }
      const empData = await empRes.json();
      setEmployee(empData);
      
      const compRes = await fetch(`${API_URL}/api/compliance?employee_id=${id}`);
      const compData = await compRes.json();
      setCompliance(compData);
      
      const assetsRes = await fetch(`${API_URL}/api/assets?assegnato_a=${id}`);
      const assetsData = await assetsRes.json();
      setAssets(assetsData);
      
      // Fetch documents linked to this employee (profile + compliance)
      const token = localStorage.getItem('token');
      const docsRes = await fetch(`${API_URL}/api/employees/${id}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const docsData = docsRes.ok ? await docsRes.json() : [];
      
      // Also fetch direct employee documents (photos)
      const empDocsRes = await fetch(`${API_URL}/api/documents?entity_type=employee&entity_id=${id}`);
      const empDocsData = empDocsRes.ok ? await empDocsRes.json() : [];
      
      // Merge and dedupe by _id
      const allDocs = [...docsData, ...empDocsData];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d._id === doc._id)
      );
      setDocuments(uniqueDocs);
      
      // Fetch appointments
      const apptRes = await fetch(`${API_URL}/api/employees/${id}/appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (apptRes.ok) {
        setAppointments(await apptRes.json());
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching employee data:', err);
      setLoading(false);
    }
  };
  
  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';

  // Delete compliance item
  const deleteComplianceItem = async (itemId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa scadenza e tutti i documenti allegati?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/compliance/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setCompliance(compliance.filter(c => c._id !== itemId));
        alert('Scadenza eliminata con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting compliance item:', err);
      alert('Errore di connessione');
    }
  };

  // Open edit compliance modal
  const openEditCompliance = (item) => {
    setEditingCompliance(item);
    setEditComplianceForm({
      titolo: item.titolo || '',
      descrizione: item.descrizione || '',
      tipo: item.tipo || '',
      categoria: item.categoria || '',
      data_rilascio: item.data_rilascio ? new Date(item.data_rilascio).toISOString().split('T')[0] : '',
      data_scadenza: item.data_scadenza ? new Date(item.data_scadenza).toISOString().split('T')[0] : '',
      ente_rilascio: item.ente_rilascio || '',
      numero_protocollo: item.numero_protocollo || ''
    });
  };

  // Update compliance item
  const updateComplianceItem = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const payload = {
      ...editComplianceForm,
      data_rilascio: editComplianceForm.data_rilascio ? new Date(editComplianceForm.data_rilascio).toISOString() : null,
      data_scadenza: editComplianceForm.data_scadenza ? new Date(editComplianceForm.data_scadenza).toISOString() : null
    };
    
    try {
      const res = await fetch(`${API_URL}/api/compliance/${editingCompliance._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setCompliance(compliance.map(c => c._id === updated._id ? updated : c));
        setEditingCompliance(null);
        alert('Scadenza aggiornata con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'aggiornamento');
      }
    } catch (err) {
      console.error('Error updating compliance item:', err);
      alert('Errore di connessione');
    }
  };

  // Delete document attachment
  const deleteDocument = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setDocuments(documents.filter(d => d._id !== docId));
        alert('Documento eliminato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Errore di connessione');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'employee');
      formData.append('entity_id', id);
      formData.append('storage_type', 'filesystem');
      formData.append('categoria', 'foto_profilo');
      
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (res.ok) {
        await fetchEmployeeData();
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante il caricamento della foto');
      }
      
      setUploadingPhoto(false);
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert('Errore durante il caricamento della foto');
      setUploadingPhoto(false);
    }
  };
  
  const handleDeleteEmployee = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/employees/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        navigate('/employees');
      } else {
        alert('Errore durante l\'eliminazione del dipendente');
      }
    } catch (err) {
      console.error('Error deleting employee:', err);
      alert('Errore durante l\'eliminazione del dipendente');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  const getUrgencyBadgeClass = (level) => {
    if (level === 'expired' || level === 'critical') return 'badge-critical';
    if (level === 'superseded') return 'badge-secondary'; // Documento superato da uno più recente
    if (level === 'warning') return 'badge-warning';
    if (level === 'soon') return 'badge-soon';
    return 'badge-ok';
  };
  
  const getUrgencyLabel = (level, daysRemaining) => {
    if (level === 'superseded') return 'Superato';
    if (level === 'expired') return 'Scaduto';
    if (level === 'critical') return `${daysRemaining}g`;
    if (level === 'warning') return `${daysRemaining}g`;
    if (level === 'soon') return `${daysRemaining}g`;
    if (level === 'no_deadline') return null;
    return `${daysRemaining}g`;
  };
  
  const getCategoryLabel = (cat) => {
    return HSE_CATEGORIES[cat] || cat;
  };
  
  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento dipendente...</p>
      </div>
    );
  }
  
  if (!employee) {
    return (
      <div className="empty-state">
        <p>Dipendente non trovato</p>
      </div>
    );
  }
  
  return (
    <div data-testid="employee-detail-page">
      <div className="mb-6">
        <Link to="/employees" className="btn btn-secondary mb-4" data-testid="back-button">
          <ArrowLeft size={20} />
          Torna ai Dipendenti
        </Link>
        
        <div className="card">
          <div className="flex items-start gap-6">
            <div className="relative">
              {documents.find(d => d.categoria === 'foto_profilo' && d.entity_type === 'employee') ? (
                <img
                  src={`${API_URL}/api/download/${documents.find(d => d.categoria === 'foto_profilo' && d.entity_type === 'employee')._id}`}
                  alt={`${employee.nome} ${employee.cognome}`}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-semibold">
                  {employee.nome?.charAt(0)}{employee.cognome?.charAt(0)}
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50">
                <Upload size={16} />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  data-testid="photo-upload-input"
                />
              </label>
            </div>
            
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">
                    {employee.nome} {employee.cognome}
                  </h1>
                  {employee.numero_matricola && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded-full mb-2">
                      <span className="text-sm font-mono font-semibold">
                        Matricola: {employee.numero_matricola}
                      </span>
                    </div>
                  )}
                  {employee.ruolo && (
                    <p className="text-lg text-muted-foreground mb-2">{employee.ruolo}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {employee.email && (
                      <span className="text-muted-foreground">📧 {employee.email}</span>
                    )}
                    {employee.telefono && (
                      <span className="text-muted-foreground">📞 {employee.telefono}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowEditModal(true)}
                    data-testid="edit-employee-button"
                  >
                    <Edit size={18} />
                    Modifica
                  </button>
                  <button
                    className="btn bg-red-500 hover:bg-red-600 text-white"
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-employee-button"
                  >
                    <Trash2 size={18} />
                    Elimina
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mb-6">
        <div className="flex gap-2 border-b border-border">
          {[
            { id: 'dati', label: 'Dati Personali', testId: 'tab-dati' },
            { id: 'documenti', label: 'Documenti HSE', badge: compliance.length, testId: 'tab-documenti' },
            { id: 'appuntamenti', label: 'Appuntamenti', badge: appointments.length, testId: 'tab-appuntamenti' },
            { id: 'asset', label: 'Asset Assegnati', badge: assets.length, testId: 'tab-asset' }
          ].map((tab) => (
            <button
              key={tab.id}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={tab.testId}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary text-primary-foreground">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      <div className="card">
        {activeTab === 'dati' && (
          <div data-testid="tab-content-dati">
            <h2 className="text-xl font-semibold mb-4">Informazioni Personali</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nome Completo</label>
                <p className="text-base font-medium mt-1">{employee.nome} {employee.cognome}</p>
              </div>
              
              {employee.codice_fiscale && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Codice Fiscale</label>
                  <p className="text-base font-medium mt-1">{employee.codice_fiscale}</p>
                </div>
              )}
              
              {employee.data_nascita && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data di Nascita</label>
                  <p className="text-base font-medium mt-1">
                    {format(new Date(employee.data_nascita), 'dd/MM/yyyy', { locale: it })}
                  </p>
                </div>
              )}
              
              {employee.luogo_nascita && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Luogo di Nascita</label>
                  <p className="text-base font-medium mt-1">{employee.luogo_nascita}</p>
                </div>
              )}
              
              {employee.data_assunzione && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data Assunzione</label>
                  <p className="text-base font-medium mt-1">
                    {format(new Date(employee.data_assunzione), 'dd/MM/yyyy', { locale: it })}
                  </p>
                </div>
              )}
              
              {employee.tipo_contratto && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Tipo Contratto</label>
                  <p className="text-base font-medium mt-1">{employee.tipo_contratto}</p>
                </div>
              )}
              
              {employee.qualifica && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Qualifica</label>
                  <p className="text-base font-medium mt-1">{employee.qualifica}</p>
                </div>
              )}
              
              {employee.sede && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <MapPin size={14} className="text-primary" />
                    Sede di Lavoro
                  </label>
                  <p className="text-base font-medium mt-1">{employee.sede}</p>
                </div>
              )}
              
              {employee.indirizzo && (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Indirizzo</label>
                  <p className="text-base font-medium mt-1">{employee.indirizzo}</p>
                </div>
              )}
              
              {employee.note && (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Note</label>
                  <p className="text-base mt-1">{employee.note}</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'documenti' && (
          <div data-testid="tab-content-documenti">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Documenti HSE</h2>
              <button
                className="btn btn-primary"
                onClick={() => setShowDocumentModal(true)}
                data-testid="add-document-button"
              >
                <Plus size={20} />
                Carica Documento
              </button>
            </div>

            {compliance.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <p>Nessun documento HSE caricato</p>
                <button 
                  className="btn btn-primary mt-4"
                  onClick={() => setShowDocumentModal(true)}
                >
                  <Plus size={20} />
                  Carica Primo Documento
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(
                  compliance.reduce((acc, item) => {
                    const cat = item.categoria || 'altro';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(item);
                    return acc;
                  }, {})
                ).map(([categoria, items]) => (
                  <div key={categoria} className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted px-4 py-3 font-semibold flex items-center gap-2">
                      <FolderOpen size={20} className="text-primary" />
                      {getCategoryLabel(categoria)}
                      <span className="ml-auto badge badge-secondary">{items.length}</span>
                    </div>
                    <div className="divide-y divide-border">
                      {items.map((item) => {
                        const itemDocs = documents.filter(d => d.entity_id === item._id);
                        return (
                          <div key={item._id} className="p-4 hover:bg-muted/30 transition-colors" data-testid="document-item">
                            <div className="flex items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold">{item.titolo}</h4>
                                  {item.days_remaining !== null ? (
                                    <span className={`badge ${getUrgencyBadgeClass(item.urgency_level)}`}>
                                      {item.urgency_level === 'superseded' 
                                        ? 'Superato' 
                                        : item.days_remaining >= 0 
                                          ? `${item.days_remaining} giorni` 
                                          : 'Scaduto'}
                                    </span>
                                  ) : (
                                    <span className="badge badge-secondary">Nessuna scadenza</span>
                                  )}
                                  {/* Edit/Delete buttons for compliance item */}
                                  {canManage && (
                                    <div className="flex gap-1 ml-auto">
                                      <button
                                        className="btn btn-secondary p-1.5"
                                        onClick={() => openEditCompliance(item)}
                                        title="Modifica scadenza"
                                      >
                                        <Edit size={14} />
                                      </button>
                                      <button
                                        className="btn btn-secondary p-1.5 text-destructive"
                                        onClick={() => deleteComplianceItem(item._id)}
                                        title="Elimina scadenza"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {item.descrizione && (
                                  <p className="text-sm text-muted-foreground mb-2">{item.descrizione}</p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                  {item.data_rilascio && (
                                    <span>📅 Inizio: {format(new Date(item.data_rilascio), 'dd/MM/yyyy', { locale: it })}</span>
                                  )}
                                  {item.data_scadenza && (
                                    <span>⏰ Scadenza: {format(new Date(item.data_scadenza), 'dd/MM/yyyy', { locale: it })}</span>
                                  )}
                                  {item.numero_protocollo && (
                                    <span className="font-mono">#{item.numero_protocollo}</span>
                                  )}
                                </div>
                                {itemDocs.length > 0 && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {itemDocs.map((doc) => (
                                      <div key={doc._id} className="inline-flex items-center gap-1 bg-primary/10 rounded-lg overflow-hidden">
                                        <a
                                          href={`${API_URL}/api/download/${doc._id}`}
                                          className="inline-flex items-center gap-2 px-3 py-1 text-primary text-sm hover:bg-primary/20 transition-colors"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          data-testid="download-attachment"
                                        >
                                          <FileText size={16} />
                                          {doc.nome_file}
                                          <span className="text-xs">({(doc.dimensione / 1024).toFixed(0)} KB)</span>
                                        </a>
                                        {canManage && (
                                          <button
                                            className="px-2 py-1 text-destructive hover:bg-destructive/20 transition-colors"
                                            onClick={() => deleteDocument(doc._id)}
                                            title="Elimina documento"
                                          >
                                            <X size={14} />
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'asset' && (
          <div data-testid="tab-content-asset">
            <h2 className="text-xl font-semibold mb-4">Asset Assegnati</h2>
            
            {assets.length === 0 ? (
              <div className="empty-state">
                <Package className="empty-state-icon" />
                <p>Nessun asset assegnato</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assets.map((asset) => (
                  <div key={asset._id} className="border border-border rounded-lg p-4" data-testid="asset-item">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="badge badge-secondary">{asset.tipo}</span>
                          <span className="badge badge-ok">{asset.stato}</span>
                        </div>
                        <h3 className="font-semibold mb-1">{asset.nome}</h3>
                        {asset.descrizione && (
                          <p className="text-sm text-muted-foreground mb-2">{asset.descrizione}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {asset.marca && <span>Marca: {asset.marca}</span>}
                          {asset.modello && <span>Modello: {asset.modello}</span>}
                          {asset.targa && <span>Targa: {asset.targa}</span>}
                          {asset.matricola && <span>Matricola: {asset.matricola}</span>}
                        </div>
                        {asset.data_assegnazione && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Assegnato il {format(new Date(asset.data_assegnazione), 'dd/MM/yyyy', { locale: it })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appuntamenti' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Appuntamenti</h2>
              <button
                className="btn btn-primary"
                onClick={() => setShowAppointmentModal(true)}
                data-testid="create-appointment-button"
              >
                <Plus size={18} />
                Nuovo Appuntamento
              </button>
            </div>
            
            {appointments.length === 0 ? (
              <div className="card text-center py-12">
                <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nessun appuntamento programmato</p>
                <button
                  className="btn btn-primary mt-4"
                  onClick={() => setShowAppointmentModal(true)}
                >
                  <Plus size={18} />
                  Aggiungi Appuntamento
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((app) => {
                  const appDate = new Date(app.data_inizio);
                  const isPast = appDate < new Date();
                  
                  return (
                    <div 
                      key={app._id}
                      className={`card border-l-4 ${
                        app.tipo === 'visita_medica' ? 'border-red-500' :
                        app.tipo === 'corso' ? 'border-blue-500' :
                        'border-gray-400'
                      } ${isPast ? 'opacity-60' : ''}`}
                    >
                      <div className="flex gap-4">
                        <div className={`w-16 text-center py-2 rounded-lg ${
                          isPast ? 'bg-muted' : 
                          app.tipo === 'visita_medica' ? 'bg-red-500 text-white' :
                          app.tipo === 'corso' ? 'bg-blue-500 text-white' :
                          'bg-primary text-white'
                        }`}>
                          <p className="text-xs uppercase">
                            {format(appDate, 'MMM', { locale: it })}
                          </p>
                          <p className="text-2xl font-bold">
                            {format(appDate, 'd')}
                          </p>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              app.tipo === 'visita_medica' ? 'bg-red-100 text-red-700' :
                              app.tipo === 'corso' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {app.tipo === 'visita_medica' ? '🏥 Visita Medica' :
                               app.tipo === 'corso' ? '📚 Corso' :
                               '📅 Appuntamento'}
                            </span>
                            {isPast && <span className="text-xs text-muted-foreground">(Passato)</span>}
                          </div>
                          <h4 className="font-semibold">{app.titolo}</h4>
                          <p className="text-sm text-muted-foreground">
                            {format(appDate, 'HH:mm', { locale: it })}
                            {app.location && ` • 📍 ${app.location}`}
                          </p>
                          {app.note && (
                            <p className="text-sm mt-2 p-2 bg-muted rounded">
                              {app.note}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {showDocumentModal && (
        <DocumentUploadModal
          employeeId={id}
          onClose={(shouldRefresh) => {
            setShowDocumentModal(false);
            if (shouldRefresh) {
              fetchEmployeeData();
            }
          }}
        />
      )}

      {/* Edit Compliance Modal */}
      {editingCompliance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Modifica Scadenza</h2>
              <button onClick={() => setEditingCompliance(null)} className="btn btn-secondary p-2">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={updateComplianceItem} className="p-6 space-y-4">
              <div className="form-group">
                <label className="form-label">Titolo *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editComplianceForm.titolo}
                  onChange={(e) => setEditComplianceForm({...editComplianceForm, titolo: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrizione</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={editComplianceForm.descrizione}
                  onChange={(e) => setEditComplianceForm({...editComplianceForm, descrizione: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Data Rilascio</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editComplianceForm.data_rilascio}
                    onChange={(e) => setEditComplianceForm({...editComplianceForm, data_rilascio: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Data Scadenza</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editComplianceForm.data_scadenza}
                    onChange={(e) => setEditComplianceForm({...editComplianceForm, data_scadenza: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select
                  className="form-input"
                  value={editComplianceForm.categoria}
                  onChange={(e) => setEditComplianceForm({...editComplianceForm, categoria: e.target.value})}
                >
                  {Object.entries(HSE_CATEGORIES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Ente Rilascio</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editComplianceForm.ente_rilascio}
                    onChange={(e) => setEditComplianceForm({...editComplianceForm, ente_rilascio: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">N. Protocollo</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editComplianceForm.numero_protocollo}
                    onChange={(e) => setEditComplianceForm({...editComplianceForm, numero_protocollo: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingCompliance(null)}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  Salva Modifiche
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Employee Modal */}
      {showEditModal && (
        <EditEmployeeModal
          employee={employee}
          onClose={(shouldRefresh) => {
            setShowEditModal(false);
            if (shouldRefresh) {
              fetchEmployeeData();
            }
          }}
        />
      )}

      {/* Create Appointment Modal */}
      {showAppointmentModal && (
        <CreateAppointmentModal
          employeeId={id}
          employeeName={`${employee.nome} ${employee.cognome}`}
          onClose={(shouldRefresh) => {
            setShowAppointmentModal(false);
            if (shouldRefresh) {
              fetchEmployeeData();
            }
          }}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="delete-confirm-modal">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Conferma Eliminazione
              </h3>
              <p className="text-gray-600 mb-6">
                Sei sicuro di voler eliminare <strong>{employee.nome} {employee.cognome}</strong>?
                <br />
                <span className="text-sm text-gray-500">Questa azione non può essere annullata.</span>
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  data-testid="cancel-delete-button"
                >
                  Annulla
                </button>
                <button
                  className="btn bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleDeleteEmployee}
                  disabled={deleting}
                  data-testid="confirm-delete-button"
                >
                  {deleting ? 'Eliminazione...' : 'Elimina Dipendente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Edit Employee Modal Component
function EditEmployeeModal({ employee, onClose }) {
  const [formData, setFormData] = useState({
    nome: employee.nome || '',
    cognome: employee.cognome || '',
    numero_matricola: employee.numero_matricola || '',
    codice_fiscale: employee.codice_fiscale || '',
    data_nascita: employee.data_nascita ? employee.data_nascita.split('T')[0] : '',
    luogo_nascita: employee.luogo_nascita || '',
    sesso: employee.sesso || '',
    data_assunzione: employee.data_assunzione ? employee.data_assunzione.split('T')[0] : '',
    ruolo: employee.ruolo || '',
    qualifica: employee.qualifica || '',
    tipo_contratto: employee.tipo_contratto || '',
    sede: employee.sede || '',
    email: employee.email || '',
    telefono: employee.telefono || '',
    indirizzo: employee.indirizzo || '',
    note: employee.note || ''
  });
  const [saving, setSaving] = useState(false);
  const [sedi, setSedi] = useState([]);
  const [newSede, setNewSede] = useState('');
  const [showAddSede, setShowAddSede] = useState(false);
  const [addingSede, setAddingSede] = useState(false);

  useEffect(() => {
    fetchSedi();
  }, []);

  const fetchSedi = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sedi`);
      if (res.ok) {
        const data = await res.json();
        setSedi(data);
      }
    } catch (err) {
      console.error('Error fetching sedi:', err);
    }
  };

  const handleAddSede = async () => {
    if (!newSede.trim()) return;
    
    try {
      setAddingSede(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sedi?nome=${encodeURIComponent(newSede.trim())}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const created = await res.json();
        setSedi([...sedi, created].sort((a, b) => a.nome.localeCompare(b.nome)));
        setFormData({ ...formData, sede: created.nome });
        setNewSede('');
        setShowAddSede(false);
      }
    } catch (err) {
      console.error('Error adding sede:', err);
    } finally {
      setAddingSede(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        data_nascita: formData.data_nascita ? new Date(formData.data_nascita).toISOString() : null,
        data_assunzione: formData.data_assunzione ? new Date(formData.data_assunzione).toISOString() : null
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/${employee._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Errore aggiornamento');
      }

      alert('Dipendente aggiornato con successo');
      onClose(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Modifica Dipendente</h2>
          <button
            onClick={() => onClose(false)}
            className="btn btn-secondary"
            data-testid="close-edit-modal"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Dati Personali */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Dati Personali</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Nome *</label>
                  <input
                    type="text"
                    name="nome"
                    className="form-input"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    data-testid="edit-nome"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Cognome *</label>
                  <input
                    type="text"
                    name="cognome"
                    className="form-input"
                    value={formData.cognome}
                    onChange={handleChange}
                    required
                    data-testid="edit-cognome"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Codice Fiscale</label>
                  <input
                    type="text"
                    name="codice_fiscale"
                    className="form-input"
                    value={formData.codice_fiscale}
                    onChange={handleChange}
                    data-testid="edit-codice-fiscale"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Numero Matricola</label>
                  <input
                    type="text"
                    name="numero_matricola"
                    className="form-input"
                    value={formData.numero_matricola}
                    onChange={handleChange}
                    data-testid="edit-matricola"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Data di Nascita</label>
                  <input
                    type="date"
                    name="data_nascita"
                    className="form-input"
                    value={formData.data_nascita}
                    onChange={handleChange}
                    data-testid="edit-data-nascita"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Luogo di Nascita</label>
                  <input
                    type="text"
                    name="luogo_nascita"
                    className="form-input"
                    value={formData.luogo_nascita}
                    onChange={handleChange}
                    data-testid="edit-luogo-nascita"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Sesso</label>
                  <select
                    name="sesso"
                    className="form-input"
                    value={formData.sesso}
                    onChange={handleChange}
                    data-testid="edit-sesso"
                  >
                    <option value="">Non specificato</option>
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dati Lavorativi */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Dati Lavorativi</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Data Assunzione</label>
                  <input
                    type="date"
                    name="data_assunzione"
                    className="form-input"
                    value={formData.data_assunzione}
                    onChange={handleChange}
                    data-testid="edit-data-assunzione"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Ruolo</label>
                  <input
                    type="text"
                    name="ruolo"
                    className="form-input"
                    value={formData.ruolo}
                    onChange={handleChange}
                    placeholder="Es: Tecnico, Amministrativo"
                    data-testid="edit-ruolo"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Qualifica</label>
                  <input
                    type="text"
                    name="qualifica"
                    className="form-input"
                    value={formData.qualifica}
                    onChange={handleChange}
                    data-testid="edit-qualifica"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo Contratto</label>
                  <select
                    name="tipo_contratto"
                    className="form-input"
                    value={formData.tipo_contratto}
                    onChange={handleChange}
                    data-testid="edit-tipo-contratto"
                  >
                    <option value="">Seleziona</option>
                    <option value="Tempo Indeterminato">Tempo Indeterminato</option>
                    <option value="Tempo Determinato">Tempo Determinato</option>
                    <option value="Apprendistato">Apprendistato</option>
                    <option value="Partita IVA">Partita IVA</option>
                    <option value="Stage">Stage</option>
                  </select>
                </div>

                {/* Sede Dropdown with Add New Option */}
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    Sede di Lavoro
                  </label>
                  {!showAddSede ? (
                    <div className="flex gap-2">
                      <select
                        name="sede"
                        className="form-input flex-1"
                        value={formData.sede}
                        onChange={handleChange}
                        data-testid="edit-sede"
                      >
                        <option value="">Seleziona sede...</option>
                        {sedi.map((s) => (
                          <option key={s._id} value={s.nome}>
                            {s.nome}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddSede(true)}
                        className="btn btn-secondary px-3"
                        title="Aggiungi nuova sede"
                        data-testid="edit-add-sede-button"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="form-input flex-1"
                        value={newSede}
                        onChange={(e) => setNewSede(e.target.value)}
                        placeholder="Nome nuova sede..."
                        autoFocus
                        data-testid="edit-new-sede-input"
                      />
                      <button
                        type="button"
                        onClick={handleAddSede}
                        className="btn btn-primary px-3"
                        disabled={addingSede || !newSede.trim()}
                        data-testid="edit-confirm-add-sede"
                      >
                        {addingSede ? '...' : 'Aggiungi'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddSede(false);
                          setNewSede('');
                        }}
                        className="btn btn-secondary px-3"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contatti */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Contatti</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="edit-email"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Telefono</label>
                  <input
                    type="tel"
                    name="telefono"
                    className="form-input"
                    value={formData.telefono}
                    onChange={handleChange}
                    data-testid="edit-telefono"
                  />
                </div>

                <div className="form-group md:col-span-2">
                  <label className="form-label">Indirizzo</label>
                  <input
                    type="text"
                    name="indirizzo"
                    className="form-input"
                    value={formData.indirizzo}
                    onChange={handleChange}
                    data-testid="edit-indirizzo"
                  />
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="form-group">
              <label className="form-label">Note</label>
              <textarea
                name="note"
                className="form-input form-textarea"
                value={formData.note}
                onChange={handleChange}
                rows={3}
                data-testid="edit-note"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-border">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              data-testid="save-employee-button"
            >
              {saving ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Appointment Modal
function CreateAppointmentModal({ employeeId, employeeName, onClose }) {
  const [formData, setFormData] = useState({
    titolo: '',
    tipo: 'visita_medica',
    data_inizio: '',
    location: '',
    note: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titolo || !formData.data_inizio) {
      alert('Compila titolo e data');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const params = new URLSearchParams({
        employee_id: employeeId,
        titolo: formData.titolo,
        tipo: formData.tipo,
        data_inizio: new Date(formData.data_inizio).toISOString()
      });
      
      if (formData.location) params.append('location', formData.location);
      if (formData.note) params.append('note', formData.note);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Errore creazione appuntamento');
      
      onClose(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-lg w-full">
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Nuovo Appuntamento</h2>
            <p className="text-sm text-muted-foreground">Per {employeeName}</p>
          </div>
          <button onClick={() => onClose(false)} className="btn btn-secondary p-2">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="form-group">
            <label className="form-label">Tipo Appuntamento</label>
            <select
              className="form-input"
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              data-testid="appointment-type"
            >
              <option value="visita_medica">🏥 Visita Medica</option>
              <option value="corso">📚 Corso Formazione</option>
              <option value="appuntamento">📅 Altro Appuntamento</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Titolo *</label>
            <input
              type="text"
              className="form-input"
              value={formData.titolo}
              onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
              placeholder={
                formData.tipo === 'visita_medica' ? 'Es: Visita periodica annuale' :
                formData.tipo === 'corso' ? 'Es: Corso antincendio' :
                'Titolo appuntamento'
              }
              required
              data-testid="appointment-title"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Data e Ora *</label>
            <input
              type="datetime-local"
              className="form-input"
              value={formData.data_inizio}
              onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })}
              required
              data-testid="appointment-date"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Luogo</label>
            <input
              type="text"
              className="form-input"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Es: Sede centrale, Studio medico..."
              data-testid="appointment-location"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Note</label>
            <textarea
              className="form-input form-textarea"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows={3}
              placeholder="Informazioni aggiuntive..."
              data-testid="appointment-notes"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button type="button" onClick={() => onClose(false)} className="btn btn-secondary">
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creazione...' : 'Crea Appuntamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmployeeDetail;
